import { useOptionsApi, useProfileStore } from '@entities/viewer'
import { pick } from '@shared/lib/collections'
import { getFirstQueryValue } from '@shared/lib/routes'

import { useDemoShiftControlStore } from './store'
import { useSlotsTrackerMetricsApi } from '../../api/demo-slots/slots-tracker-metrics.hook'
import { useSlotsTrackerApi } from '../../api/demo-slots/slots-tracker.hook'
import { useWaitingBookingsApi } from '../../api/demo-slots/waiting-bookings.hook'

type TrackedSlot = 'prev' | 'current' | 'next'

interface ButtonTrackerRefreshReturn {
  seconds: number
  disabled: ComputedRef<boolean>
  setRefresh: () => void
}
export const useButtonTrackerRefresh = (): ButtonTrackerRefreshReturn => {
  const seconds = 60

  const route = useRoute()
  const router = useRouter()
  const profileStore = useProfileStore()
  const demoShiftControlStore = useDemoShiftControlStore()

  const disabled = computed(() => demoShiftControlStore.isShiftControlBusy)

  const detectTrackedSlot = (): TrackedSlot | null => {
    const queryFrom = getFirstQueryValue(route.query?.from)
    if (!queryFrom) return null
    if (queryFrom === profileStore.getPrevTimeSlotFrom) return 'prev'
    if (queryFrom === profileStore.getCurrentTimeSlotFrom) return 'current'
    if (queryFrom === profileStore.getNextTimeSlotFrom) return 'next'
    return null
  }

  const getSlotRange = (slot: TrackedSlot): { from: string | null, to: string | null } => {
    if (slot === 'prev') return { from: profileStore.getPrevTimeSlotFrom, to: profileStore.getPrevTimeSlotTo }
    if (slot === 'next') return { from: profileStore.getNextTimeSlotFrom, to: profileStore.getNextTimeSlotTo }
    return { from: profileStore.getCurrentTimeSlotFrom, to: profileStore.getCurrentTimeSlotTo }
  }

  const syncTrackedSlotToQuery = async (slot: TrackedSlot): Promise<void> => {
    const { from, to } = getSlotRange(slot)
    if (!from || !to) return

    const queryFrom = getFirstQueryValue(route.query?.from)
    const queryTo = getFirstQueryValue(route.query?.to)
    if (queryFrom === from && queryTo === to) return

    demoShiftControlStore.setFilterFrom(from)
    demoShiftControlStore.setFilterTo(to)

    await router.push({
      query: pick({
        ...demoShiftControlStore.getFilterParams,
        current_page: String(demoShiftControlStore.getCurrentPage),
        per_page: String(demoShiftControlStore.getPerPage),
      }),
    })
  }

  const setRefresh = async (): Promise<void> => {
    const trackedSlot = detectTrackedSlot()

    await useOptionsApi()

    if (trackedSlot) await syncTrackedSlotToQuery(trackedSlot)

    await useSlotsTrackerMetricsApi()
    await useSlotsTrackerApi()
    await useWaitingBookingsApi()
  }
  return {
    seconds,
    disabled,
    setRefresh,
  }
}
